import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { Users, Star, Gift, Heart, Award, Plus, Search, TrendingUp, Crown, MessageSquare, ThumbsUp, Building2, Pencil, Trash2 } from 'lucide-react';
import api from '@/lib/api';

export default function CRMLoyaltyPage() {
  const [programs, setPrograms] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [redemptionItems, setRedemptionItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddProgram, setShowAddProgram] = useState(false);
  const [showEnrollMember, setShowEnrollMember] = useState(false);
  const [showAddRedemption, setShowAddRedemption] = useState(false);
  const [showEditRedemption, setShowEditRedemption] = useState(false);
  const [editingRedemption, setEditingRedemption] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  
  // Form states
  const [newProgram, setNewProgram] = useState({
    name: '',
    code: '',
    description: '',
    points_per_currency: 1,
    welcome_points: 500
  });
  
  const [newEnrollment, setNewEnrollment] = useState({
    program_id: '',
    guest_id: ''
  });
  
  const [newRedemption, setNewRedemption] = useState({
    name: '',
    description: '',
    category: 'amenity',
    points_required: 1000,
    cash_value: null
  });

  // Get property_id from auth context
  const getSelectedPropertyId = () => {
    const storedProperty = localStorage.getItem('hms_current_property');
    if (storedProperty) {
      try {
        const prop = JSON.parse(storedProperty);
        return prop.id || '';
      } catch {
        return '';
      }
    }
    return '';
  };
  
  const selectedProperty = getSelectedPropertyId();

  useEffect(() => {
    loadData();
  }, [selectedProperty]);

  useEffect(() => {
    if (selectedProgram) {
      loadProgramDetails(selectedProgram.id);
    }
  }, [selectedProgram]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [programsRes, statsRes] = await Promise.all([
        api.get('/crm/loyalty/programs'),
        api.get(`/crm/stats${selectedProperty ? `?property_id=${selectedProperty}` : ''}`)
      ]);
      
      setPrograms(programsRes.data || []);
      setStats(statsRes.data);
      
      if (programsRes.data?.length > 0 && !selectedProgram) {
        setSelectedProgram(programsRes.data[0]);
      }
      
      // Load feedback if property selected
      if (selectedProperty) {
        const feedbackRes = await api.get(`/crm/feedback?property_id=${selectedProperty}`);
        setFeedback(feedbackRes.data || []);
      }
    } catch (error) {
      console.error('Error loading CRM data:', error);
      toast.error('Failed to load CRM data');
    } finally {
      setLoading(false);
    }
  };

  const loadProgramDetails = async (programId) => {
    try {
      const [tiersRes, membersRes, redemptionRes] = await Promise.all([
        api.get(`/crm/loyalty/programs/${programId}/tiers`),
        api.get(`/crm/loyalty/accounts?program_id=${programId}`),
        api.get(`/crm/loyalty/programs/${programId}/redemption-catalog`)
      ]);
      
      setTiers(tiersRes.data || []);
      setMembers(membersRes.data || []);
      setRedemptionItems(redemptionRes.data || []);
    } catch (error) {
      console.error('Error loading program details:', error);
    }
  };

  const handleCreateProgram = async () => {
    try {
      await api.post('/crm/loyalty/programs', newProgram);
      toast.success('Loyalty program created');
      setShowAddProgram(false);
      setNewProgram({ name: '', code: '', description: '', points_per_currency: 1, welcome_points: 500 });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create program');
    }
  };

  const handleEnrollMember = async () => {
    try {
      await api.post('/crm/loyalty/accounts', newEnrollment);
      toast.success('Member enrolled successfully');
      setShowEnrollMember(false);
      setNewEnrollment({ program_id: '', guest_id: '' });
      if (selectedProgram) {
        loadProgramDetails(selectedProgram.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enroll member');
    }
  };

  const handleAddRedemptionItem = async () => {
    if (!selectedProgram) return;
    try {
      await api.post(`/crm/loyalty/programs/${selectedProgram.id}/redemption-catalog`, newRedemption);
      toast.success('Redemption item added');
      setShowAddRedemption(false);
      setNewRedemption({ name: '', description: '', category: 'amenity', points_required: 1000, cash_value: null });
      loadProgramDetails(selectedProgram.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add item');
    }
  };

  const handleEditRedemptionItem = async () => {
    if (!editingRedemption) return;
    try {
      await api.put(`/crm/loyalty/redemption-catalog/${editingRedemption.id}`, {
        name: editingRedemption.name,
        description: editingRedemption.description,
        category: editingRedemption.category,
        points_required: editingRedemption.points_required,
        cash_value: editingRedemption.cash_value,
      });
      toast.success('Reward updated');
      setShowEditRedemption(false);
      setEditingRedemption(null);
      loadProgramDetails(selectedProgram.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update item');
    }
  };

  const handleDeleteRedemptionItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this reward?')) return;
    try {
      await api.delete(`/crm/loyalty/redemption-catalog/${itemId}`);
      toast.success('Reward deleted');
      loadProgramDetails(selectedProgram.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete item');
    }
  };

  const handleRespondToFeedback = async (feedbackId, response) => {
    try {
      await api.put(`/crm/feedback/${feedbackId}/respond?response_text=${encodeURIComponent(response)}`);
      toast.success('Response saved');
      loadData();
    } catch (error) {
      toast.error('Failed to save response');
    }
  };

  const getTierBadge = (tier) => {
    if (!tier) return null;
    return (
      <Badge style={{ backgroundColor: tier.color || '#6b7280', color: '#fff' }}>
        {tier.name || tier.loyalty_tiers?.name || 'Member'}
      </Badge>
    );
  };

  const getRatingStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star key={i} className={`h-4 w-4 ${i < rating ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`} />
    ));
  };

  const filteredMembers = members.filter(m => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    const guestName = m.guests ? `${m.guests.first_name} ${m.guests.last_name}`.toLowerCase() : '';
    return m.member_number?.toLowerCase().includes(search) || guestName.includes(search) || m.guests?.email?.toLowerCase().includes(search);
  });

  return (
    <div className="p-6 space-y-6" data-testid="crm-loyalty-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">CRM & Loyalty</h1>
          <p className="text-slate-500">Manage guest relationships, loyalty programs, and feedback</p>
        </div>
        <Dialog open={showAddProgram} onOpenChange={setShowAddProgram}>
          <DialogTrigger asChild>
            <Button data-testid="create-program-btn">
              <Plus className="h-4 w-4 mr-2" />
              Create Program
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Loyalty Program</DialogTitle>
              <DialogDescription>Set up a new loyalty program with default tiers</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Program Name</Label>
                <Input value={newProgram.name} onChange={(e) => setNewProgram({...newProgram, name: e.target.value})} placeholder="e.g., Hotel Rewards" />
              </div>
              <div>
                <Label>Program Code</Label>
                <Input value={newProgram.code} onChange={(e) => setNewProgram({...newProgram, code: e.target.value})} placeholder="e.g., REWARDS" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={newProgram.description} onChange={(e) => setNewProgram({...newProgram, description: e.target.value})} placeholder="Describe the program benefits..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Points per ETB Spent</Label>
                  <Input type="number" value={newProgram.points_per_currency} onChange={(e) => setNewProgram({...newProgram, points_per_currency: parseFloat(e.target.value) || 1})} />
                </div>
                <div>
                  <Label>Welcome Bonus Points</Label>
                  <Input type="number" value={newProgram.welcome_points} onChange={(e) => setNewProgram({...newProgram, welcome_points: parseInt(e.target.value) || 0})} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddProgram(false)}>Cancel</Button>
              <Button onClick={handleCreateProgram}>Create Program</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Members</p>
                <p className="text-2xl font-bold">{stats?.total_members || 0}</p>
              </div>
              <Users className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Recent Enrollments</p>
                <p className="text-2xl font-bold">{stats?.recent_enrollments || 0}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Average Rating</p>
                <p className="text-2xl font-bold">{stats?.average_rating?.toFixed(1) || '0.0'}</p>
              </div>
              <Star className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Feedback Count</p>
                <p className="text-2xl font-bold">{stats?.feedback_count || 0}</p>
              </div>
              <MessageSquare className="h-8 w-8 text-indigo-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="loyalty" className="space-y-4">
        <TabsList>
          <TabsTrigger value="loyalty">Loyalty Programs</TabsTrigger>
          <TabsTrigger value="members">Members</TabsTrigger>
          <TabsTrigger value="redemptions">Redemption Catalog</TabsTrigger>
          <TabsTrigger value="feedback">Guest Feedback</TabsTrigger>
        </TabsList>

        {/* Loyalty Programs Tab */}
        <TabsContent value="loyalty">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Program List */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-700">Programs</h3>
              {programs.length === 0 ? (
                <Card>
                  <CardContent className="p-6 text-center text-slate-500">
                    <Award className="mx-auto h-12 w-12 mb-4 opacity-50" />
                    <p>No loyalty programs yet</p>
                    <p className="text-sm">Create your first program</p>
                  </CardContent>
                </Card>
              ) : (
                programs.map(program => (
                  <Card key={program.id} className={`cursor-pointer transition-all ${selectedProgram?.id === program.id ? 'ring-2 ring-blue-500' : 'hover:shadow-md'}`} onClick={() => setSelectedProgram(program)}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold">{program.name}</h4>
                          <p className="text-sm text-slate-500">{program.code}</p>
                        </div>
                        {program.is_active ? <Badge className="bg-emerald-100 text-emerald-800">Active</Badge> : <Badge variant="secondary">Inactive</Badge>}
                      </div>
                      <div className="mt-3 flex items-center gap-4 text-sm text-slate-600">
                        <span>{program.points_per_currency} pts/ETB</span>
                        <span>+{program.welcome_points} welcome</span>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>

            {/* Program Details & Tiers */}
            <div className="lg:col-span-2">
              {selectedProgram ? (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>{selectedProgram.name}</CardTitle>
                        <CardDescription>{selectedProgram.description || 'No description'}</CardDescription>
                      </div>
                      <Crown className="h-6 w-6 text-amber-500" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <h4 className="font-semibold mb-4">Membership Tiers</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {tiers.map(tier => (
                        <Card key={tier.id} style={{ borderTop: `4px solid ${tier.color}` }}>
                          <CardContent className="p-4 text-center">
                            <h5 className="font-semibold">{tier.name}</h5>
                            <p className="text-2xl font-bold mt-2">{tier.min_points?.toLocaleString()}</p>
                            <p className="text-xs text-slate-500">points required</p>
                            <div className="mt-2">
                              <Badge variant="outline">{tier.points_multiplier}x points</Badge>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                    
                    {/* Tier Distribution */}
                    <div className="mt-6">
                      <h4 className="font-semibold mb-4">Member Distribution</h4>
                      <div className="space-y-3">
                        {stats?.members_by_tier?.map((t, idx) => (
                          <div key={`${t.tier}-${idx}`} className="flex items-center gap-3">
                            <div className="w-20 text-sm font-medium">{t.tier}</div>
                            <div className="flex-1">
                              <Progress value={stats.total_members > 0 ? (t.count / stats.total_members) * 100 : 0} className="h-2" />
                            </div>
                            <div className="w-12 text-sm text-right">{t.count}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-10 text-center text-slate-500">
                    <Award className="mx-auto h-12 w-12 mb-4 opacity-50" />
                    <p>Select a program to view details</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Members Tab */}
        <TabsContent value="members">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Loyalty Members</CardTitle>
                <CardDescription>Manage enrolled members and their points</CardDescription>
              </div>
              <div className="flex gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input placeholder="Search members..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 w-64" />
                </div>
                <Dialog open={showEnrollMember} onOpenChange={setShowEnrollMember}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="enroll-member-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      Enroll Member
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Enroll New Member</DialogTitle>
                      <DialogDescription>Add a guest to the loyalty program</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label>Program</Label>
                        <Select value={newEnrollment.program_id} onValueChange={(v) => setNewEnrollment({...newEnrollment, program_id: v})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select program" />
                          </SelectTrigger>
                          <SelectContent>
                            {programs.filter(p => p.is_active).map(p => (
                              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Guest ID</Label>
                        <Input value={newEnrollment.guest_id} onChange={(e) => setNewEnrollment({...newEnrollment, guest_id: e.target.value})} placeholder="Enter guest ID" />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowEnrollMember(false)}>Cancel</Button>
                      <Button onClick={handleEnrollMember}>Enroll</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {filteredMembers.length === 0 ? (
                <div className="text-center py-10 text-slate-500">
                  <Users className="mx-auto h-12 w-12 mb-4 opacity-50" />
                  <p>No members found</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Member</TableHead>
                      <TableHead>Member #</TableHead>
                      <TableHead>Tier</TableHead>
                      <TableHead>Points Balance</TableHead>
                      <TableHead>Lifetime Points</TableHead>
                      <TableHead>Enrolled</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredMembers.map(member => (
                      <TableRow key={member.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedMember(member)}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar>
                              <AvatarFallback>
                                {member.guests?.first_name?.[0]}{member.guests?.last_name?.[0]}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{member.guests?.first_name} {member.guests?.last_name}</p>
                              <p className="text-sm text-slate-500">{member.guests?.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono">{member.member_number}</TableCell>
                        <TableCell>{getTierBadge(member.loyalty_tiers || member)}</TableCell>
                        <TableCell className="font-semibold text-blue-600">{member.points_balance?.toLocaleString()}</TableCell>
                        <TableCell>{member.lifetime_points?.toLocaleString()}</TableCell>
                        <TableCell>{member.enrollment_date}</TableCell>
                        <TableCell>
                          {member.status === 'active' ? <Badge className="bg-emerald-100 text-emerald-800">Active</Badge> : <Badge variant="secondary">{member.status}</Badge>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Redemption Catalog Tab */}
        <TabsContent value="redemptions">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Redemption Catalog</CardTitle>
                <CardDescription>Rewards members can redeem with their points</CardDescription>
              </div>
              <Dialog open={showAddRedemption} onOpenChange={setShowAddRedemption}>
                <DialogTrigger asChild>
                  <Button size="sm" disabled={!selectedProgram} data-testid="add-redemption-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Item
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Redemption Item</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label>Item Name</Label>
                      <Input value={newRedemption.name} onChange={(e) => setNewRedemption({...newRedemption, name: e.target.value})} placeholder="e.g., Free Night Stay" />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea value={newRedemption.description} onChange={(e) => setNewRedemption({...newRedemption, description: e.target.value})} />
                    </div>
                    <div>
                      <Label>Category</Label>
                      <Select value={newRedemption.category} onValueChange={(v) => setNewRedemption({...newRedemption, category: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="free_night">Free Night</SelectItem>
                          <SelectItem value="upgrade">Room Upgrade</SelectItem>
                          <SelectItem value="amenity">Amenity</SelectItem>
                          <SelectItem value="fb_credit">F&B Credit</SelectItem>
                          <SelectItem value="partner">Partner Reward</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Points Required</Label>
                        <Input type="number" value={newRedemption.points_required} onChange={(e) => setNewRedemption({...newRedemption, points_required: parseInt(e.target.value) || 0})} />
                      </div>
                      <div>
                        <Label>Cash Value (ETB)</Label>
                        <Input type="number" value={newRedemption.cash_value || ''} onChange={(e) => setNewRedemption({...newRedemption, cash_value: parseFloat(e.target.value) || null})} placeholder="Optional" />
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowAddRedemption(false)}>Cancel</Button>
                    <Button onClick={handleAddRedemptionItem}>Add Item</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {!selectedProgram ? (
                <div className="text-center py-10 text-slate-500">
                  <Gift className="mx-auto h-12 w-12 mb-4 opacity-50" />
                  <p>Select a program first to manage redemption items</p>
                </div>
              ) : redemptionItems.length === 0 ? (
                <div className="text-center py-10 text-slate-500">
                  <Gift className="mx-auto h-12 w-12 mb-4 opacity-50" />
                  <p>No redemption items configured</p>
                  <p className="text-sm">Add rewards that members can redeem</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {redemptionItems.map(item => (
                    <Card key={item.id}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <Badge variant="outline" className="mb-2">{item.category.replace('_', ' ')}</Badge>
                            <h4 className="font-semibold">{item.name}</h4>
                            <p className="text-sm text-slate-500 mt-1 truncate">{item.description || 'No description'}</p>
                          </div>
                          <div className="flex gap-1 ml-2">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => { setEditingRedemption({...item}); setShowEditRedemption(true); }}
                              data-testid={`edit-reward-${item.id}`}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => handleDeleteRedemptionItem(item.id)}
                              data-testid={`delete-reward-${item.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                        <div className="mt-4 flex items-center justify-between">
                          <span className="text-lg font-bold text-purple-600">{item.points_required?.toLocaleString()} pts</span>
                          {item.cash_value && <span className="text-sm text-slate-500">≈ {item.cash_value} ETB</span>}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Edit Redemption Dialog */}
        <Dialog open={showEditRedemption} onOpenChange={setShowEditRedemption}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Reward</DialogTitle>
            </DialogHeader>
            {editingRedemption && (
              <div className="space-y-4 py-4">
                <div>
                  <Label>Item Name</Label>
                  <Input value={editingRedemption.name} onChange={(e) => setEditingRedemption({...editingRedemption, name: e.target.value})} />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea value={editingRedemption.description || ''} onChange={(e) => setEditingRedemption({...editingRedemption, description: e.target.value})} />
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={editingRedemption.category} onValueChange={(v) => setEditingRedemption({...editingRedemption, category: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="free_night">Free Night</SelectItem>
                      <SelectItem value="upgrade">Room Upgrade</SelectItem>
                      <SelectItem value="amenity">Amenity</SelectItem>
                      <SelectItem value="fb_credit">F&B Credit</SelectItem>
                      <SelectItem value="partner">Partner Reward</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Points Required</Label>
                    <Input type="number" value={editingRedemption.points_required} onChange={(e) => setEditingRedemption({...editingRedemption, points_required: parseInt(e.target.value) || 0})} />
                  </div>
                  <div>
                    <Label>Cash Value (ETB)</Label>
                    <Input type="number" value={editingRedemption.cash_value || ''} onChange={(e) => setEditingRedemption({...editingRedemption, cash_value: parseFloat(e.target.value) || null})} />
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditRedemption(false)}>Cancel</Button>
              <Button onClick={handleEditRedemptionItem} data-testid="save-edit-reward-btn">Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Feedback Tab */}
        <TabsContent value="feedback">
          <Card>
            <CardHeader>
              <CardTitle>Guest Feedback</CardTitle>
              <CardDescription>Reviews and feedback from guests</CardDescription>
            </CardHeader>
            <CardContent>
              {!selectedProperty ? (
                <div className="text-center py-10 text-slate-500">
                  <Building2 className="mx-auto h-12 w-12 mb-4 opacity-50" />
                  <p>Select a property to view feedback</p>
                </div>
              ) : feedback.length === 0 ? (
                <div className="text-center py-10 text-slate-500">
                  <MessageSquare className="mx-auto h-12 w-12 mb-4 opacity-50" />
                  <p>No feedback received yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {feedback.map(fb => (
                    <Card key={fb.id}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <Avatar>
                              <AvatarFallback>
                                {fb.guests?.first_name?.[0]}{fb.guests?.last_name?.[0]}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{fb.guests?.first_name} {fb.guests?.last_name}</p>
                              <div className="flex items-center gap-1 mt-1">
                                {getRatingStars(fb.overall_rating)}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge variant={fb.status === 'responded' ? 'default' : 'outline'}>{fb.status}</Badge>
                            <p className="text-xs text-slate-500 mt-1">{new Date(fb.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                        {fb.comments && (
                          <p className="mt-3 text-slate-600 italic">"{fb.comments}"</p>
                        )}
                        {fb.response && (
                          <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                            <p className="text-sm font-medium text-blue-900">Your Response:</p>
                            <p className="text-sm text-blue-700">{fb.response}</p>
                          </div>
                        )}
                        {fb.status !== 'responded' && (
                          <div className="mt-3">
                            <Button size="sm" variant="outline" onClick={() => {
                              const response = prompt('Enter your response:');
                              if (response) handleRespondToFeedback(fb.id, response);
                            }}>
                              <MessageSquare className="h-4 w-4 mr-2" />
                              Respond
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
